import { useMemo, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { fetchTimeline, type TimelineResponse } from '../../api/admin.js';
import {
  aggregateByDimension,
  aggregateTotals,
  summarizeTurns,
} from '../../lib/session-stats.js';
import {
  formatDurationMs,
  formatNumber,
  nanoToUsdCompact,
  shortId,
} from '../../lib/format.js';

const MAX_COMPARE = 3;

const KIND_COLORS: Record<string, string> = {
  llm: 'var(--k-llm)',
  tts: 'var(--k-tts)',
  stt: 'var(--k-stt)',
  pronunciation: 'var(--k-pronunciation)',
  voice: 'var(--k-voice)',
  tool_call: 'var(--k-tool_call)',
  retrieval: 'var(--k-retrieval)',
  guardrail: 'var(--k-guardrail)',
  agent_decision: 'var(--k-agent_decision)',
  http: 'var(--k-http)',
  db_query: 'var(--k-db_query)',
  pipeline_stage: 'var(--k-pipeline_stage)',
  module_step: 'var(--k-module_step)',
  job: 'var(--k-job)',
};
function kindColor(k: string): string {
  return KIND_COLORS[k] ?? 'var(--text-tertiary)';
}

export function SessionsComparePage(): ReactElement {
  const [params] = useSearchParams();
  const idsRaw = params.get('ids') ?? '';
  const ids = useMemo(() => {
    const split = idsRaw.split(',').map((s) => s.trim()).filter(Boolean);
    return Array.from(new Set(split)).slice(0, MAX_COMPARE);
  }, [idsRaw]);

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['admin', 'timeline', id],
      queryFn: () => fetchTimeline(id),
    })),
  });

  if (ids.length === 0) {
    return (
      <div className="loading">
        Faltan ids para comparar. <Link to="/sessions">Volver al listado</Link>.
      </div>
    );
  }

  const ready = queries.every((q) => q.isSuccess);
  const anyError = queries.find((q) => q.isError);

  if (anyError) {
    return (
      <div className="loading">
        Error cargando timelines: {anyError.error?.message ?? 'desconocido'}
      </div>
    );
  }

  if (!ready) {
    return <div className="loading">Cargando timelines…</div>;
  }

  const datasets: ReadonlyArray<TimelineResponse> = queries
    .map((q) => q.data)
    .filter((d) => Boolean(d));

  return (
    <div className="sess-compare">
      <header className="page-header">
        <div>
          <h1 className="page-title">Comparar sesiones</h1>
          <p className="page-sub">
            {datasets.length} sesiones · datos en vivo · <Link to="/sessions">volver</Link>
          </p>
        </div>
      </header>

      <SummaryTable datasets={datasets} />
      <KindCostMatrix datasets={datasets} />
      <KindTimeMatrix datasets={datasets} />
      <TurnsOverlay datasets={datasets} />
    </div>
  );
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return (sorted[mid - 1]! + sorted[mid]!) / 2;
  return sorted[mid]!;
}

interface DatasetsProps {
  datasets: ReadonlyArray<TimelineResponse>;
}

function SummaryTable({ datasets }: DatasetsProps): ReactElement {
  // Total cost / duration / turns / errors salen del session response del
  // backend — mismo cálculo que usa la lista y el header del detalle (excluye
  // kinds container `session`/`turn`/`composite`/`job` para no double-count
  // y para no inflar con jobs post-sesión). spanCount sí es client-side.
  const totals = datasets.map((d) => {
    const agg = aggregateTotals(d.spans);
    // Fase 34: vistas operativas usan raw provider cost (sin markup). El
    // backend expone `totalCostRawNano` aliasado de `SUM(spans.provider_cost_nano)`.
    // Fallback al total con markup si el backend no lo trajo (compat).
    let costNano = 0n;
    try {
      costNano = BigInt(d.session.totalCostRawNano ?? d.session.totalCostNano);
    } catch {
      costNano = 0n;
    }
    return {
      sessionId: d.session.id,
      moduleId: d.session.moduleId,
      userEmail: d.session.userEmail,
      startedAt: d.session.startedAt,
      durationMs: d.session.totalDurationMs,
      turnCount: d.session.turnCount,
      costNano,
      spanCount: agg.spanCount,
      errorCount: d.session.errorCount,
    };
  });

  const durations = totals.map((t) => t.durationMs).filter((n) => n > 0);
  const medianDuration = median(durations);
  const costsNumber = totals.map((t) =>
    t.costNano < BigInt(Number.MAX_SAFE_INTEGER)
      ? Number(t.costNano)
      : Number(t.costNano / 1_000_000n) * 1_000_000,
  );
  const medianCost = median(costsNumber.filter((n) => n > 0));

  function deltaPctLabel(value: number, ref: number): { label: string; tone: 'pos' | 'neg' | 'flat' } {
    if (ref === 0 || value === 0) return { label: '—', tone: 'flat' };
    const pct = ((value - ref) / ref) * 100;
    if (Math.abs(pct) < 5) return { label: `≈ ${pct.toFixed(0)}%`, tone: 'flat' };
    const sign = pct > 0 ? '+' : '';
    return {
      label: `${sign}${pct.toFixed(0)}%`,
      tone: pct > 0 ? 'neg' : 'pos',
    };
  }

  return (
    <section className="sess-compare-section" data-testid="compare-summary">
      <h2 className="sess-compare-h">Resumen</h2>
      <div className="sess-compare-grid" style={{ gridTemplateColumns: `160px repeat(${totals.length}, 1fr)` }}>
        <div className="sess-compare-key">session</div>
        {totals.map((t) => (
          <div key={`hdr-${t.sessionId}`} className="sess-compare-cell">
            <Link to={`/sessions/${t.sessionId}`} className="mono">
              {shortId(t.sessionId, 14)}
            </Link>
            <div className="sess-compare-sub mono">{t.moduleId ?? '—'}</div>
          </div>
        ))}

        <div className="sess-compare-key">started</div>
        {totals.map((t) => (
          <div key={`s-${t.sessionId}`} className="sess-compare-cell mono">
            {new Date(t.startedAt).toLocaleString()}
          </div>
        ))}

        <div className="sess-compare-key">user</div>
        {totals.map((t) => (
          <div key={`u-${t.sessionId}`} className="sess-compare-cell">{t.userEmail}</div>
        ))}

        <div className="sess-compare-key">duration</div>
        {totals.map((t) => {
          const delta = deltaPctLabel(t.durationMs, medianDuration);
          return (
            <div key={`d-${t.sessionId}`} className="sess-compare-cell mono">
              {formatDurationMs(t.durationMs)}
              {totals.length > 1 ? (
                <span className={`sess-compare-delta tone-${delta.tone}`}>{delta.label}</span>
              ) : null}
            </div>
          );
        })}

        <div className="sess-compare-key">cost</div>
        {totals.map((t, i) => {
          const delta = deltaPctLabel(costsNumber[i] ?? 0, medianCost);
          return (
            <div key={`c-${t.sessionId}`} className="sess-compare-cell mono">
              {nanoToUsdCompact(t.costNano.toString())}
              {totals.length > 1 ? (
                <span className={`sess-compare-delta tone-${delta.tone}`}>{delta.label}</span>
              ) : null}
            </div>
          );
        })}

        <div className="sess-compare-key">turns</div>
        {totals.map((t) => (
          <div key={`t-${t.sessionId}`} className="sess-compare-cell mono">
            {formatNumber(t.turnCount)}
          </div>
        ))}

        <div className="sess-compare-key">spans</div>
        {totals.map((t) => (
          <div key={`sp-${t.sessionId}`} className="sess-compare-cell mono">
            {formatNumber(t.spanCount)}
          </div>
        ))}

        <div className="sess-compare-key">errors</div>
        {totals.map((t) => (
          <div key={`e-${t.sessionId}`} className="sess-compare-cell">
            {t.errorCount > 0 ? (
              <span className="badge b-error">{t.errorCount}</span>
            ) : (
              <span className="muted mono">0</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function KindCostMatrix({ datasets }: DatasetsProps): ReactElement {
  const perSession = datasets.map((d) => ({
    sessionId: d.session.id,
    buckets: aggregateByDimension(d.spans, 'kind'),
  }));
  const allKinds = new Set<string>();
  for (const ps of perSession) for (const b of ps.buckets) allKinds.add(b.key);

  const totalsByKind: Record<string, number> = {};
  for (const ps of perSession) {
    for (const b of ps.buckets) {
      const val =
        b.costNano < BigInt(Number.MAX_SAFE_INTEGER)
          ? Number(b.costNano)
          : Number(b.costNano / 1_000_000n) * 1_000_000;
      totalsByKind[b.key] = (totalsByKind[b.key] ?? 0) + val;
    }
  }
  const sortedKinds = Array.from(allKinds).sort(
    (a, b) => (totalsByKind[b] ?? 0) - (totalsByKind[a] ?? 0),
  );

  return (
    <section className="sess-compare-section" data-testid="compare-cost-matrix">
      <h2 className="sess-compare-h">Costo por kind</h2>
      <div className="sess-compare-bars">
        {perSession.map((ps) => {
          const total = ps.buckets.reduce((acc, b) => {
            const val =
              b.costNano < BigInt(Number.MAX_SAFE_INTEGER)
                ? Number(b.costNano)
                : Number(b.costNano / 1_000_000n) * 1_000_000;
            return acc + val;
          }, 0);
          return (
            <div key={ps.sessionId} className="sess-compare-row">
              <Link to={`/sessions/${ps.sessionId}`} className="sess-compare-rowlabel mono">
                {shortId(ps.sessionId, 12)}
              </Link>
              <div className="sess-stack-bar">
                {sortedKinds.map((k) => {
                  const b = ps.buckets.find((x) => x.key === k);
                  if (!b || total === 0) return null;
                  const val =
                    b.costNano < BigInt(Number.MAX_SAFE_INTEGER)
                      ? Number(b.costNano)
                      : Number(b.costNano / 1_000_000n) * 1_000_000;
                  const pct = (val / total) * 100;
                  if (pct < 0.1) return null;
                  return (
                    <div
                      key={k}
                      className="sess-stack-seg"
                      style={{ width: `${pct}%`, background: kindColor(k) }}
                      title={`${k}: ${nanoToUsdCompact(b.costNano.toString())} (${pct.toFixed(1)}%)`}
                    />
                  );
                })}
              </div>
              <span className="sess-compare-rowtotal mono">
                {nanoToUsdCompact(total.toString())}
              </span>
            </div>
          );
        })}
      </div>
      <ul className="sess-chart-legend sess-chart-legend-row" style={{ marginTop: 'var(--s-3)' }}>
        {sortedKinds.map((k) => (
          <li key={k} className="sess-legend-row">
            <span className="sess-legend-swatch" style={{ background: kindColor(k) }} />
            <span className="sess-legend-key mono">{k}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function KindTimeMatrix({ datasets }: DatasetsProps): ReactElement {
  const perSession = datasets.map((d) => ({
    sessionId: d.session.id,
    buckets: aggregateByDimension(d.spans, 'kind'),
  }));
  const allKinds = new Set<string>();
  for (const ps of perSession) for (const b of ps.buckets) allKinds.add(b.key);
  const totals: Record<string, number> = {};
  for (const ps of perSession) for (const b of ps.buckets) {
    totals[b.key] = (totals[b.key] ?? 0) + b.durationMs;
  }
  const sortedKinds = Array.from(allKinds).sort(
    (a, b) => (totals[b] ?? 0) - (totals[a] ?? 0),
  );
  return (
    <section className="sess-compare-section" data-testid="compare-time-matrix">
      <h2 className="sess-compare-h">Tiempo por kind</h2>
      <div className="sess-compare-bars">
        {perSession.map((ps) => {
          const total = ps.buckets.reduce((acc, b) => acc + b.durationMs, 0);
          return (
            <div key={ps.sessionId} className="sess-compare-row">
              <Link to={`/sessions/${ps.sessionId}`} className="sess-compare-rowlabel mono">
                {shortId(ps.sessionId, 12)}
              </Link>
              <div className="sess-stack-bar">
                {sortedKinds.map((k) => {
                  const b = ps.buckets.find((x) => x.key === k);
                  if (!b || total === 0) return null;
                  const pct = (b.durationMs / total) * 100;
                  if (pct < 0.1) return null;
                  return (
                    <div
                      key={k}
                      className="sess-stack-seg"
                      style={{ width: `${pct}%`, background: kindColor(k) }}
                      title={`${k}: ${formatDurationMs(b.durationMs)} (${pct.toFixed(1)}%)`}
                    />
                  );
                })}
              </div>
              <span className="sess-compare-rowtotal mono">{formatDurationMs(total)}</span>
            </div>
          );
        })}
      </div>
      <ul className="sess-chart-legend sess-chart-legend-row" style={{ marginTop: 'var(--s-3)' }}>
        {sortedKinds.map((k) => (
          <li key={k} className="sess-legend-row">
            <span className="sess-legend-swatch" style={{ background: kindColor(k) }} />
            <span className="sess-legend-key mono">{k}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TurnsOverlay({ datasets }: DatasetsProps): ReactElement {
  const perSession = datasets.map((d, i) => {
    const turns = summarizeTurns(d.spans);
    return {
      sessionId: d.session.id,
      turns,
      color: SERIES_COLORS[i % SERIES_COLORS.length]!,
    };
  });
  const maxTurns = Math.max(1, ...perSession.map((p) => p.turns.length));
  const maxDuration = Math.max(
    1,
    ...perSession.flatMap((p) => p.turns.map((t) => t.durationMs)),
  );

  const width = 640;
  const height = 200;
  const padX = 32;
  const padY = 16;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  return (
    <section className="sess-compare-section" data-testid="compare-turns-overlay">
      <h2 className="sess-compare-h">Duración por turn</h2>
      <div className="chart-host">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          {/* gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => {
            const y = padY + innerH - f * innerH;
            return (
              <g key={f}>
                <line
                  x1={padX}
                  x2={width - padX}
                  y1={y}
                  y2={y}
                  stroke="var(--border-subtle)"
                  strokeDasharray="2 4"
                />
                <text x={6} y={y + 3} fontSize={9} fill="var(--text-tertiary)" fontFamily="var(--font-mono)">
                  {formatDurationMs(maxDuration * f)}
                </text>
              </g>
            );
          })}
          {perSession.map((ps) => {
            if (ps.turns.length === 0) return null;
            const stepX = ps.turns.length > 1 ? innerW / (ps.turns.length - 1) : innerW;
            const points = ps.turns
              .map((t, i) => {
                const x = padX + i * stepX;
                const y = padY + innerH - (t.durationMs / maxDuration) * innerH;
                return `${x},${y}`;
              })
              .join(' ');
            return (
              <g key={ps.sessionId}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={ps.color}
                  strokeWidth={1.5}
                />
                {ps.turns.map((t, i) => {
                  const x = padX + i * stepX;
                  const y = padY + innerH - (t.durationMs / maxDuration) * innerH;
                  return (
                    <circle key={t.turnId} cx={x} cy={y} r={2.5} fill={ps.color}>
                      <title>{`${shortId(ps.sessionId, 8)} · turn ${t.turnIndex}: ${formatDurationMs(t.durationMs)}`}</title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
          <text
            x={padX}
            y={height - 4}
            fontSize={9}
            fill="var(--text-tertiary)"
            fontFamily="var(--font-mono)"
          >
            turn 1
          </text>
          <text
            x={width - padX}
            y={height - 4}
            fontSize={9}
            fill="var(--text-tertiary)"
            fontFamily="var(--font-mono)"
            textAnchor="end"
          >
            turn {maxTurns}
          </text>
        </svg>
      </div>
      <ul className="sess-chart-legend sess-chart-legend-row" style={{ marginTop: 'var(--s-3)' }}>
        {perSession.map((ps) => (
          <li key={ps.sessionId} className="sess-legend-row">
            <span className="sess-legend-swatch" style={{ background: ps.color }} />
            <span className="sess-legend-key mono">{shortId(ps.sessionId, 12)}</span>
            <span className="sess-legend-val mono">{ps.turns.length} turns</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const SERIES_COLORS = [
  'oklch(65% 0.20 290)',
  'oklch(70% 0.18 50)',
  'oklch(65% 0.16 140)',
];
